import { hasAnalyticsConsent } from "@/lib/consent";

declare global {

  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __shugertVitalsStarted?: boolean;
  }
}

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

const FIRST_TOUCH_KEY = "shugert_attribution_first_v2";
const SESSION_KEY = "shugert_attribution_session_v2";
const MAX_PATHS = 24;

type UtmKey = (typeof UTM_KEYS)[number];

export type AttributionTouch = {
  page: string;
  referrer: string;
  seen_at: string;
} & Partial<Record<UtmKey, string>>;

export type AttributionSnapshot = {
  first_touch?: AttributionTouch;
  last_touch?: AttributionTouch;
  session_started_at?: string;
  session_count: number;
  pages_viewed: string[];
  money_pages_viewed: string[];
  resource_clusters_viewed: string[];
};

export type AttributionPayload = {
  landing_page?: string;
  landing_referrer?: string;
  first_seen_at?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
};

export type MarketingEventName =
  | "marketing_cta_click"
  | "pricing_entry_clicked"
  | "quote_form_view"
  | "quote_entry_selected"
  | "quote_form_started"
  | "quote_form_submitted"
  | "quote_form_error"
  | "contact_form_view"
  | "contact_form_started"
  | "contact_form_submitted"
  | "contact_form_error"
  | "audit_viewed"
  | "audit_started"
  | "audit_completed"
  | "audit_failed"
  | "audit_unlocked"
  | "audit_recommendation_clicked"
  | "resource_search"
  | "resource_filter_selected"
  | "resource_scope_click"
  | "case_study_scope_click"
  | "language_switched";

const MONEY_PAGE_PREFIXES = [
  "/shopify-",
  "/analytics-ai",
  "/retainer",
  "/pricing",
  "/services",
  "/geo-readiness",
  "/es/rendimiento",
  "/es/cro-shopify",
  "/es/seo-tecnico-shopify",
  "/es/migracion-shopify",
  "/es/desarrollo-shopify",
  "/es/integraciones-shopify",
  "/es/desarrollo-apps-shopify",
  "/es/agencia-shopify-plus",
  "/es/analitica-ai",
  "/es/retainer",
  "/es/precios",
  "/es/servicios",
  "/es/auditoria-geo",
] as const;

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function uniqueTail(values: string[], value: string): string[] {
  if (!value) return values.slice(-MAX_PATHS);
  return [...values.filter((item) => item !== value), value].slice(-MAX_PATHS);
}

function resourceCluster(path: string): string | null {
  if (/\/resources\/performance|\/es\/recursos\/rendimiento/.test(path)) return "performance";
  if (/\/resources\/conversion|\/es\/recursos\/conversion/.test(path)) return "conversion";
  if (/\/resources\/technical-seo|\/es\/recursos\/seo-tecnico/.test(path)) return "technical-seo";
  if (/\/resources\/migration|\/es\/recursos\/migracion/.test(path)) return "migration";
  if (/\/resources\/|\/es\/recursos\//.test(path)) return "resource";
  return null;
}

function currentTouch(): AttributionTouch {
  const params = new URLSearchParams(window.location.search);
  const touch: AttributionTouch = {
    page: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || "(direct)",
    seen_at: new Date().toISOString(),
  };
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) touch[key] = value.slice(0, 500);
  }
  return touch;
}

/**
 * Capture a durable first touch and a session-level last touch. The stored
 * path history intentionally excludes form values and other PII.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const now = new Date().toISOString();
    const touch = currentTouch();
    let first = safeParse<AttributionTouch>(window.localStorage.getItem(FIRST_TOUCH_KEY));
    if (!first) {
      first = touch;
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(first));
    }

    const existing = safeParse<AttributionSnapshot>(window.sessionStorage.getItem(SESSION_KEY));
    const path = window.location.pathname;
    const cluster = resourceCluster(path);
    const snapshot: AttributionSnapshot = {
      first_touch: first,
      last_touch: touch,
      session_started_at: existing?.session_started_at ?? now,
      session_count: existing?.session_count ?? 1,
      pages_viewed: uniqueTail(existing?.pages_viewed ?? [], path),
      money_pages_viewed: MONEY_PAGE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
        ? uniqueTail(existing?.money_pages_viewed ?? [], path)
        : existing?.money_pages_viewed ?? [],
      resource_clusters_viewed: cluster
        ? uniqueTail(existing?.resource_clusters_viewed ?? [], cluster)
        : existing?.resource_clusters_viewed ?? [],
    };
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(snapshot));
  } catch {
    /* Storage may be unavailable or blocked. Marketing behavior must continue. */
  }
}

export function readAttribution(): AttributionSnapshot {
  if (typeof window === "undefined") {
    return { session_count: 0, pages_viewed: [], money_pages_viewed: [], resource_clusters_viewed: [] };
  }
  try {
    const first = safeParse<AttributionTouch>(window.localStorage.getItem(FIRST_TOUCH_KEY)) ?? undefined;
    const session = safeParse<AttributionSnapshot>(window.sessionStorage.getItem(SESSION_KEY));
    return {
      first_touch: first ?? session?.first_touch,
      last_touch: session?.last_touch,
      session_started_at: session?.session_started_at,
      session_count: session?.session_count ?? 1,
      pages_viewed: session?.pages_viewed ?? [],
      money_pages_viewed: session?.money_pages_viewed ?? [],
      resource_clusters_viewed: session?.resource_clusters_viewed ?? [],
    };
  } catch {
    return { session_count: 0, pages_viewed: [], money_pages_viewed: [], resource_clusters_viewed: [] };
  }
}

/** Flat payload compatible with the existing quotes attribution columns. */
export function getAttributionPayload(): AttributionPayload {
  const snapshot = readAttribution();
  const first = snapshot.first_touch;
  const campaignTouch = UTM_KEYS.some((key) => snapshot.last_touch?.[key])
    ? snapshot.last_touch
    : first;
  const payload: AttributionPayload = {
    landing_page: first?.page,
    landing_referrer: first?.referrer,
    first_seen_at: first?.seen_at,
  };
  for (const key of UTM_KEYS) {
    const value = campaignTouch?.[key] ?? first?.[key];
    if (value) payload[key] = value;
  }
  return payload;
}

/** Human-readable, non-PII context for records without attribution columns. */
export function getAttributionContext(): string {
  const snapshot = readAttribution();
  const payload = getAttributionPayload();
  const lines = [
    payload.landing_page ? `First landing: ${payload.landing_page}` : null,
    payload.landing_referrer ? `First referrer: ${payload.landing_referrer}` : null,
    payload.utm_source ? `UTM source: ${payload.utm_source}` : null,
    payload.utm_medium ? `UTM medium: ${payload.utm_medium}` : null,
    payload.utm_campaign ? `UTM campaign: ${payload.utm_campaign}` : null,
    snapshot.last_touch?.page ? `Last touch: ${snapshot.last_touch.page}` : null,
    snapshot.money_pages_viewed.length ? `Money pages: ${snapshot.money_pages_viewed.join(", ")}` : null,
    snapshot.resource_clusters_viewed.length ? `Resource clusters: ${snapshot.resource_clusters_viewed.join(", ")}` : null,
  ].filter((line): line is string => Boolean(line));
  return lines.join("\n");
}

function pushAnalyticsEvent(event: string, payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  // No pre-consent queueing: events sent before an analytics grant (or after
  // a rejection) are dropped, never replayed.
  if (!hasAnalyticsConsent()) return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", event, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...payload });
    }
  } catch {
    /* Analytics must never break product behavior. */
  }
}


/** Track a marketing event without sending names, emails, messages or URLs entered in forms. */
export function trackMarketingEvent(
  event: MarketingEventName,
  payload: Record<string, string | number | boolean | null | undefined> = {},
): void {
  if (typeof window === "undefined") return;
  const snapshot = readAttribution();
  const attribution = getAttributionPayload();
  pushAnalyticsEvent(event, {
    ...payload,
    page_path: window.location.pathname,
    page_title: document.title,
    page_locale: window.location.pathname.startsWith("/es") ? "es" : "en",
    landing_page: attribution.landing_page,
    landing_referrer: attribution.landing_referrer,
    first_seen_at: attribution.first_seen_at,
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    utm_term: attribution.utm_term,
    utm_content: attribution.utm_content,
    gclid: attribution.gclid,
    fbclid: attribution.fbclid,
    last_touch_page: snapshot.last_touch?.page,
    money_pages_viewed: snapshot.money_pages_viewed.join("|"),
    resource_clusters_viewed: snapshot.resource_clusters_viewed.join("|"),
  });
}

type CalendlyClickParams = {
  page: string;
  location: string;
  locale: "en" | "es";
  destination: string;
};

export function trackCalendlyClick(params: CalendlyClickParams): void {
  trackMarketingEvent("marketing_cta_click", {
    event_category: "cta",
    event_label: `${params.page}:${params.location}`,
    cta_type: "calendly",
    cta_page: params.page,
    cta_location: params.location,
    cta_locale: params.locale,
    cta_destination: params.destination,
  });
}
